import { ArrowRight } from '@phosphor-icons/react';
import { OnboardingStep } from '@reelplicate/api-client/model';
import { Button, Field, FilterChip, Inline, Stack, TextInput } from '@reelplicate/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import { finishPath } from './steps';

const SOURCES = [
  'Google',
  'TikTok',
  'Instagram',
  'YouTube',
  'LinkedIn',
  'Friend or colleague',
  'Podcast',
  'Newsletter',
  'Other',
];

/** Focuses "Other"'s input as it appears - the user just asked to type. */
const focusOnMount = (input: HTMLInputElement | null) => input?.focus();

/**
 * Figma "Onboarding — 7 · How did you find us" (128:1262): optional, one
 * Filter Chip choice in a wrapping row, then Skip and "Finish setup".
 * Choosing "Other" reveals a text input to say where.
 */
export function SourcePage() {
  const navigate = useNavigate();
  const [source, setSource] = useState<string | null>(null);
  const [otherSource, setOtherSource] = useState('');
  const { advance, saving } = useOnboardingProgress();
  // The answer isn't stored yet; finishing (or skipping) completes onboarding.
  const finish = async () => {
    if (await advance(OnboardingStep.COMPLETED)) await navigate(finishPath('settingUp'));
  };

  return (
    <OnboardingCard
      loose
      onSubmit={(event) => {
        event.preventDefault();
        void finish();
      }}
    >
      <OnboardingHeader
        loose
        eyebrow="One last thing"
        title="How did you find us?"
        subtitle="Optional — it helps us find more teams like yours."
      />
      <Stack gap={5}>
        <Inline as="div" role="group" aria-label="How did you find us?" gap={2} wrap>
          {SOURCES.map((option) => (
            <FilterChip
              key={option}
              selected={source === option}
              onSelectedChange={(on) => setSource(on ? option : null)}
            >
              {option}
            </FilterChip>
          ))}
        </Inline>
        {source === 'Other' && (
          <Field label="Where did you hear about us?">
            <TextInput
              ref={focusOnMount}
              value={otherSource}
              onChange={(event) => setOtherSource(event.target.value)}
              placeholder="e.g. a Reddit thread"
              maxLength={100}
            />
          </Field>
        )}
      </Stack>
      <OnboardingActions>
        <Button
          type="button"
          variant="ghost"
          size="lg"
          onClick={() => void finish()}
          disabled={saving}
        >
          Skip
        </Button>
        <Button type="submit" size="lg" trailingIcon={ArrowRight} loading={saving}>
          Finish setup
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
